import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";
import Lottie from "lottie-react";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import safeSecureAnim from "../assets/lottie/Safe_and_secure.json";
import userVerifiedAnim from "../assets/lottie/User_Verified.json";
import referEarnAnim from "../assets/lottie/Refer_and_Earn.json";

// ─── Helper: Lottie color filter ──────────────────────────────────────────────
// Replaces specific colors in Lottie: primary strokes #F97316, secondary fills #FFFFFF
const getLottieColorFilter = () => ({
  filter: [
    {
      id: "color-filter",
      type: "color",
      colors: {
        "#000000": "#F97316",
        "#666666": "#FFFFFF",
      },
    },
  ],
});

// ─── Shared sub-components ───────────────────────────────────────────────────

function StarRow() {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} width="12" height="12" viewBox="0 0 20 20" fill="#F97316">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function Pill({ children }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 8,
      background: "rgba(255,255,255,0.05)",
      borderLeft: "2px solid #F97316",
      fontSize: isMobile ? "0.75rem" : "0.8rem",
      color: "#D1D5DB",
      lineHeight: 1.4,
      wordWrap: "break-word",
      overflowWrap: "break-word",
    }}>
      {children}
    </div>
  );
}

function ReviewCard({ quote, author }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={{
      padding: isMobile ? "12px 14px" : "14px 16px",
      borderRadius: 10,
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.1)",
    }}>
      <StarRow />
      <p style={{
        fontSize: isMobile ? "0.72rem" : "0.78rem",
        color: "#D1D5DB",
        lineHeight: 1.55,
        margin: "8px 0 6px",
        wordWrap: "break-word",
        overflowWrap: "break-word",
      }}>
        "{quote}"
      </p>
      <p style={{
        fontSize: isMobile ? "0.65rem" : "0.72rem",
        color: "#6B7280",
        wordWrap: "break-word",
        overflowWrap: "break-word",
      }}>— {author}</p>
    </div>
  );
}

// ─── Slides data ─────────────────────────────────────────────────────────────

const SLIDES = [
  {
    anim: safeSecureAnim,
    headline: "Not a rupee lost without proof.",
    subtext: "Landlords can only deduct what they can prove. Every claim needs photo evidence from the move-in report — the same report you both signed on day one. No photo. No deduction. No arguments.",
    pills: [
      "📸  Move-in photos sealed & timestamped forever",
      "✅  You approve every deduction before it moves",
      "⚖️  Disputes resolved in 10 days. Guaranteed.",
    ],
    review: {
      quote: "My landlord claimed the walls needed repainting. Orello pulled up the move-in photos. Full ₹75,000 back in 4 days.",
      author: "Sneha T., Tenant · Bandra, Mumbai",
    },
  },
  {
    anim: userVerifiedAnim,
    headline: "Your property. Documented before they move in.",
    subtext: "Room-by-room photos, timestamped and sealed the day keys are handed over. If there's damage at move-out, the evidence already exists. Nobody can say it was already like that.",
    pills: [
      "🪪  Aadhaar + PAN verified before agreement",
      "📄  Court-valid e-stamped agreement — every time",
      "📸  Property condition locked on day one — no one can edit it",
    ],
    review: {
      quote: "Tenant denied all damage at move-out. The timestamped move-in report ended the argument in one day.",
      author: "Ramesh K., Landlord · Kothrud, Pune",
    },
  },
  {
    anim: referEarnAnim,
    headline: "Every deal clean. Every client happy.",
    subtext: "Signed agreements, verified parties, documented handover — all on record. No WhatsApp arguments at 11pm. No chasing signatures. No awkward deposit fights between your clients.",
    pills: [
      "📋  Agreements, deposits, reports — all in one place",
      "💰  Earn up to ₹1,000 when tenant secures deposit",
      "🤝  Refer brokers — earn bonus per successful referral",
    ],
    review: {
      quote: "8 deals last month. Zero follow-up disputes. My clients refer me because the whole process just works.",
      author: "Imran K., Broker · Andheri, Mumbai",
    },
  },
];

// ─── Rotating Trust Bar ───────────────────────────────────────────────────────

const TRUST_MESSAGES = [
  "Your deposit is held securely by Orello. Not with the landlord. Not with us. Released only when you agree.",
  "Every tenant on Orello is Aadhaar + PAN verified before they sign. Your property, your terms — backed by a court-valid agreement.",
  "Manage your entire rental lifecycle in one place — agreements, deposits, move-in reports, move-out, settlement. Free to join.",
];

function TrustBar() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % TRUST_MESSAGES.length);
        setVisible(true);
      }, 350);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{
      marginTop: 16,
      paddingTop: 14,
      borderTop: "1px solid rgba(245,245,245,0.06)",
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      minHeight: 36,
      width: "100%",
      overflow: "hidden",
    }}>
      <Lock size={11} style={{ color: "#6B7280", marginTop: 1, flexShrink: 0 }} />
      <p style={{
        fontSize: 11,
        color: "#9CA3AF",
        lineHeight: 1.5,
        textAlign: "left",
        transition: "opacity 0.35s ease",
        opacity: visible ? 1 : 0,
        margin: 0,
        wordWrap: "break-word",
        overflowWrap: "break-word",
      }}>
        {TRUST_MESSAGES[idx]}
      </p>
    </div>
  );
}

// ─── Lottie Carousel Sidebar ─────────────────────────────────────────────────

function LoginCarousel() {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const goTo = useCallback((idx) => {
    if (idx === active) return;
    setFading(true);
    setTimeout(() => {
      setActive(idx);
      setFading(false);
    }, 300);
  }, [active]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((active + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [active, goTo]);

  const slide = SLIDES[active];
  const lottieSize = isMobile ? 150 : 220;

  return (
    <div style={{
      background: "#0a0a0a",
      borderTop: "1px solid rgba(249,115,22,0.12)",
      borderBottom: "1px solid rgba(249,115,22,0.12)",
      padding: isMobile ? "24px 16px" : "40px 48px",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 24 : 48,
      alignItems: "center",
      position: "relative",
      minHeight: "auto",
      justifyContent: "center",
    }}>
      {/* Left — Lottie */}
      <div style={{
        flexShrink: 0,
        width: lottieSize,
        height: lottieSize,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "opacity 0.3s ease",
        opacity: fading ? 0 : 1,
        order: isMobile ? -1 : 0,
      }}>
        <Lottie
          animationData={slide.anim}
          loop
          style={{ width: lottieSize, height: lottieSize }}
        />
      </div>

      {/* Right — text */}
      <div style={{
        flex: isMobile ? "1 1 auto" : 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        transition: "opacity 0.3s ease",
        opacity: fading ? 0 : 1,
        width: isMobile ? "100%" : "auto",
        maxWidth: isMobile ? "100%" : "none",
      }}>
        <h2 style={{
          fontSize: isMobile ? "1.1rem" : "1.3rem",
          fontWeight: 700,
          color: "#FFFFFF",
          lineHeight: 1.3,
          margin: 0,
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}>
          {slide.headline}
        </h2>

        <p style={{
          fontSize: isMobile ? "0.75rem" : "0.85rem",
          color: "#D1D5DB",
          lineHeight: 1.6,
          margin: 0,
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}>
          {slide.subtext}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {slide.pills.map((pill, i) => (
            <Pill key={i}>{pill}</Pill>
          ))}
        </div>

        <ReviewCard quote={slide.review.quote} author={slide.review.author} />
      </div>

      {/* Dot indicators — bottom center */}
      <div style={{
        position: "absolute",
        bottom: 14,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        gap: 6,
        paddingLeft: 16,
        paddingRight: 16,
      }}>
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
            style={{
              width: i === active ? 20 : 6,
              height: 6,
              borderRadius: 3,
              background: i === active ? "#F97316" : "rgba(255,255,255,0.2)",
              border: "none",
              padding: 0,
              cursor: "pointer",
              transition: "width 0.3s ease, background 0.3s ease",
              flexShrink: 0,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Login page ─────────────────────────────────────────────────────────

export default function Login() {
  const { loginWithOtp } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const userType = searchParams.get("type") || null;
  const { t } = useI18n();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const subtitleMap = {
    tenant: t("login_subtitle_tenant"),
    landlord: t("login_subtitle_landlord"),
    broker: t("login_subtitle_broker"),
  };
  const subtitle = subtitleMap[userType] || t("login_subtitle_default");

  const signIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      localStorage.setItem("orello_token", data.token);
      await loginWithOtp(data.user, data.token);
      if (data.needs_phone_verification) {
        toast.info("Please verify your phone number first.");
        nav(`/verify-otp?phone=${encodeURIComponent(data.phone)}`);
        return;
      }
      toast.success("Welcome back.");
      // Navigate to dashboard
      // The popstate listener in AuthContext will prevent going back
      nav("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Invalid credentials."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#080808", color: "#F5F5F5", position: "relative", overflow: "hidden" }}>
      {/* ambient glow */}
      <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(249,115,22,0.08), transparent 70%)", pointerEvents: "none" }} />

      <Header />

      <div className="flex-1 flex flex-col items-center justify-center py-8 gap-8" style={{ width: "100%", overflow: "hidden" }}>

        {/* Carousel — shown FIRST on mobile, LAST on desktop */}
        {isMobile && (
          <div className="w-full order-first">
            <LoginCarousel />
          </div>
        )}

        {/* Login form — centered */}
        <div className="w-full max-w-sm px-4">
          <div
            className="p-8 space-y-5"
            style={{ background: "#111111", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 16, color: "#F5F5F5" }}
            data-testid="login-form"
          >
            <div>
              <h1 className="text-2xl font-bold font-display">{t("login_welcome")}</h1>
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            </div>

            <form onSubmit={signIn} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-300">{t("login_phone_label")}</label>
                <input
                  data-testid="login-identifier"
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t("login_phone_placeholder")}
                  autoComplete="username"
                  style={{ background: "#1A1A1A", color: "#F5F5F5" }}
                  className="mt-1 w-full border border-gray-600 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">{t("login_password_label")}</label>
                  <Link to="/forgot-password" className="text-xs text-[#F97316] hover:underline">{t("login_forgot")}</Link>
                </div>
                <div className="mt-1 relative">
                  <input
                    data-testid="login-password"
                    required
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("login_password_label")}
                    autoComplete="current-password"
                    style={{ background: "#1A1A1A", color: "#F5F5F5" }}
                    className="w-full border border-gray-600 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", padding: 4 }}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                data-testid="login-submit"
                disabled={busy}
                className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white rounded-lg font-medium disabled:opacity-50"
              >
                {busy ? t("login_btn_loading") : t("login_btn")}
              </button>
            </form>

            {/* Rotating trust bar */}
            <TrustBar />
          </div>
        </div>

        {/* Carousel — shown LAST on desktop */}
        {!isMobile && (
          <div className="w-full">
            <LoginCarousel />
          </div>
        )}

      </div>
    </div>
  );
}
